// Google Analytics utility for RentVibe

// Get the GA measurement ID from environment
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Check if GA is properly configured
const isAnalyticsAvailable = () => {
  return typeof window !== 'undefined' &&
         window.gtag &&
         GA_MEASUREMENT_ID &&
         GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX';
};

// Initialize GA4 with enhanced measurements
export const initializeGA4 = () => {
  if (!isAnalyticsAvailable()) return;

  // Configure GA4 with enhanced measurements
  window.gtag('config', GA_MEASUREMENT_ID, {
    // Enhanced measurements
    allow_google_signals: true,
    allow_ad_features: false,

    // Enable enhanced measurements for better location tracking
    enhanced_measurement: {
      page_views: true,
      scrolls: true,
      clicks: true,
      file_downloads: false,
      form_interactions: true,
      search: false,
      video: false,
    },

    // Custom settings for location tracking
    custom_map: {
      'dimension1': 'user_city',
      'dimension2': 'user_region',
      'dimension3': 'user_country',
    },

    // Send page view
    page_title: document.title,
    page_location: window.location.href,

    // Enable debug mode in development
    debug_mode: process.env.NODE_ENV === 'development',
  });

  console.log('GA4 initialized with measurement ID:', GA_MEASUREMENT_ID);
};

// Track page views
export const trackPageView = (pageTitle, pagePath) => {
  if (!isAnalyticsAvailable()) return;

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_title: pageTitle,
    page_location: window.location.href,
    page_path: pagePath,
  });
};

// Track custom events
export const trackEvent = (eventName, parameters = {}) => {
  if (!isAnalyticsAvailable()) return;

  window.gtag('event', eventName, {
    ...parameters,
    timestamp: new Date().toISOString(),
  });
};

// Track user location (with privacy - only city/region level)
export const trackUserLocation = async (latitude, longitude) => {
  if (!isAnalyticsAvailable()) return;

  try {
    // Get city/region from reverse geocoding (optional)
    let city = 'Unknown';
    let region = 'Unknown';
    let country = 'Unknown';

    try {
      // Use a free geocoding service or Mapbox reverse geocoding
      const response = await fetch(`https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}&types=place,region,country`);
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        // Extract city, region, country from the response
        const features = data.features;
        const cityFeature = features.find(f => f.place_type.includes('place'));
        const regionFeature = features.find(f => f.place_type.includes('region'));
        const countryFeature = features.find(f => f.place_type.includes('country'));

        city = cityFeature?.text || city;
        region = regionFeature?.text || region;
        country = countryFeature?.text || country;
      }
    } catch (geoError) {
      console.warn('Reverse geocoding failed:', geoError);
    }

    // Send GA4-compatible location data
    window.gtag('event', 'page_view', {
      page_location: window.location.href,
      page_title: document.title,
      // GA4 automatically collects geo from IP, but we can enhance with custom dimensions
      custom_map: {
        'dimension1': 'user_city',
        'dimension2': 'user_region',
        'dimension3': 'user_country',
        'metric1': Math.round(latitude * 100) / 100, // Rounded lat
        'metric2': Math.round(longitude * 100) / 100, // Rounded lng
      }
    });

    // Also send a custom event with location data
    window.gtag('event', 'user_location_detected', {
      // GA4 custom parameters
      city: city,
      region: region,
      country: country,
      latitude: Math.round(latitude * 100) / 100,
      longitude: Math.round(longitude * 100) / 100,
      // GA4 will use IP-based geo for real-time reports
      // But we can also set custom user properties
      custom_user_properties: {
        user_city: city,
        user_region: region,
        user_country: country,
      }
    });

    // Set user properties for future sessions
    window.gtag('set', 'user_properties', {
      user_city: city,
      user_region: region,
      user_country: country,
      location_accuracy: 'approximate', // For privacy compliance
    });

    console.log('Location tracked:', { city, region, country, latitude, longitude });

  } catch (error) {
    console.error('Error tracking user location:', error);
  }
};

// Track review submission
export const trackReviewSubmitted = (reviewData) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('review_submitted', {
    recommendation: reviewData.recommend,
    safety_rating: reviewData.safetyFeel,
    cleanliness_rating: reviewData.areaCleanliness,
    traffic_rating: reviewData.trafficLevel,
    has_landlord_feedback: !!reviewData.landlordEthicsEmoji,
    location_approximate: `${Math.round(reviewData.latitude * 10) / 10}, ${Math.round(reviewData.longitude * 10) / 10}`,
  });
};

// Track cluster viewing
export const trackClusterViewed = (clusterData) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('building_reviews_viewed', {
    review_count: clusterData.reviewCount,
    avg_rating: clusterData.avgRating,
    recommendation_ratio: clusterData.recommendationRatio,
    location_approximate: `${Math.round(clusterData.center.lat * 10) / 10}, ${Math.round(clusterData.center.lng * 10) / 10}`,
  });
};

// Track AI summary generation
export const trackAISummaryGenerated = (reviewCount) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('ai_summary_generated', {
    review_count: reviewCount,
  });
};

// Track search/filter usage
export const trackFilterUsed = (filterType, value) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('filter_applied', {
    filter_type: filterType, // 'sort', 'filter'
    filter_value: value, // 'newest', 'recommended', etc.
  });
};

// Track map interactions
export const trackMapInteraction = (interactionType, details = {}) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('map_interaction', {
    interaction_type: interactionType, // 'click', 'zoom', 'location_found'
    ...details,
  });
};

// Track time spent on reviews
export const trackReviewTimeSpent = (reviewId, timeSpentSeconds) => {
  if (!isAnalyticsAvailable()) return;

  trackEvent('review_engagement', {
    review_id: reviewId,
    time_spent_seconds: timeSpentSeconds,
  });
};

// Initialize analytics with user consent
export const initializeAnalytics = (consentGiven = false) => {
  if (!isAnalyticsAvailable()) return;

  // Set consent status
  window.gtag('consent', 'default', {
    analytics_storage: consentGiven ? 'granted' : 'denied',
    ad_storage: 'denied', // We don't use ads
  });

  if (consentGiven) {
    trackEvent('analytics_consent_granted');
  }
};

// Export the measurement ID for debugging
export const getGAMeasurementId = () => GA_MEASUREMENT_ID;

// Analytics dashboard data (for admin view)
export const getAnalyticsSummary = () =>
  // This would typically fetch from Google Analytics API
  // For now, return mock data structure
  ({
    totalReviews: 0,
    totalUsers: 0,
    topNeighborhoods: [],
    reviewTrends: [],
    safetyRatings: {
      excellent: 0, // 8-10
      good: 0, // 6-7
      poor: 0, // 0-5
    },
    commonIssues: [],
    recommendationRate: 0,
  });
